import React, {useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {ReactComponent as PlusIcon} from 'assets/svg/plus.svg';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {FileFormPropsCallback} from 'components/common/XlsxFileForm';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';

import {ControlContainer, HeadContainer, Wrapper} from '../common/styled';
import {XlsxFileModal} from '../common/UploadModal';

type Props = {
    value: string[];
    onChange: (value: string[]) => void;
};

const normalizeCode = (code: string) => code.trim().replace(/\s/g, '');

export const ResultMarkingCodes: React.FC<Props> = ({value, onChange}) => {
    const {t} = useTranslation();
    const [code, setCode] = useState('');
    const [isScannerOpen, setScannerOpen] = useState(false);
    const [isUploadOpen, setUploadOpen] = useState(false);
    const [isScanPending, setScanPending] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [uploadedCodes, setUploadedCodes] = useState<string[]>([]);

    const addCodes = (codes: string[]) => {
        const nextCodes = codes.map(normalizeCode).filter(Boolean);
        onChange([...value, ...nextCodes.filter((item) => !value.includes(item))]);
    };

    const addCode = () => {
        addCodes([code]);
        setCode('');
    };

    const onScan = async (scannedCode: string) => {
        setScanPending(true);
        addCodes([scannedCode]);
        setScanPending(false);
    };

    const onFileChange = ({data, file: nextFile, isValid}: FileFormPropsCallback) => {
        setFile(nextFile ?? null);
        setUploadedCodes(isValid ? data.map(({code}) => code) : []);
    };

    const columns = useMemo(
        () =>
            mapTableColumns([
                {
                    id: 'markingCode',
                    header: t('pages.documents.form.dropout.markingCode'),
                    minWidth: 100,
                },
                {
                    id: 'action',
                    header: t('pages.products.grid.action'),
                    width: 20,
                },
            ]),
        [t],
    );

    const rows = [
        {
            markingCode: (
                <Input
                    value={code}
                    placeholder={t('pages.documents.form.dropout.markingCode')}
                    onChange={(event) => setCode(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            addCode();
                        }
                    }}
                />
            ),
            action: (
                <Button
                    style={{width: 'fit-content'}}
                    size="sm"
                    type="button"
                    variant={ButtonVariant.Secondary}
                    disabled={!normalizeCode(code)}
                    onClick={addCode}
                    icon={<Icon source={PlusIcon} width={16} height={16} />}
                >
                    {t('components.form.add')}
                </Button>
            ),
        },
        ...value.map((item) => ({
            markingCode: item,
            action: (
                <Button
                    size="sm"
                    type="button"
                    variant={ButtonVariant.Text}
                    onClick={() => onChange(value.filter((codeItem) => codeItem !== item))}
                >
                    <Icon source={CrossIcon} width={16} height={16} fillColor="#FF4B55" />
                </Button>
            ),
        })),
    ];

    return (
        <Wrapper>
            <HeadContainer>
                <h3>{t('pages.documents.form.dropout.resultMarkingCodes')}</h3>
                <ControlContainer>
                    <Button type="button" onClick={() => setScannerOpen(true)}>
                        {t('components.scan.button')}
                        <Icon source={ScanIcon} width={20} height={20} />
                    </Button>
                    <Button
                        type="button"
                        variant={ButtonVariant.Outline}
                        onClick={() => setUploadOpen(true)}
                    >
                        {t('components.upload.button')}
                        <Icon source={UploadIcon} width={16} height={16} />
                    </Button>
                </ControlContainer>
            </HeadContainer>
            <Table columns={columns} data={rows} hideSetting cellContainerSettings="width: 100%;" />
            <QRScannerModal
                open={isScannerOpen}
                pending={isScanPending}
                onClose={() => setScannerOpen(false)}
                onScan={onScan}
                onManual={onScan}
            />
            <XlsxFileModal
                open={isUploadOpen}
                initialFile={file}
                onClose={() => setUploadOpen(false)}
                onFileChange={onFileChange}
                submit={(valid, pending) => (
                    <Button
                        type="button"
                        disabled={!valid || pending}
                        onClick={() => {
                            addCodes(uploadedCodes);
                            setUploadOpen(false);
                        }}
                    >
                        {t('components.form.add')}
                    </Button>
                )}
            />
        </Wrapper>
    );
};
